import type { Experiment } from 'types';
import { Fragment, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useExperimentTimeseries } from '@/state/experiments/useExperimentTimeseries';
import { PillTabs } from '../../../ConsolePanel';
import { seriesColor, seriesColorForVariant, variantColorOrder } from './control-options';
import {
  AXIS_TICK,
  LEGEND_STYLE,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
} from './StatisticsChart';
import {
  buildCumulativeSeries,
  buildDailySeries,
  buildSignificanceSeries,
  countSignificanceCrossings,
  sinceSessionsStart,
} from './rate-series';

/**
 * How the result behaved over time, rather than only where it ended up.
 *
 * Every other figure on this page is a single cumulative number, which cannot show
 * whether a rate has settled, whether a win was a novelty effect that reverted, or
 * whether significance was reached once and held or crossed the line repeatedly. Those
 * are the three questions that decide whether a result can be acted on, so they get
 * three views of the same daily data.
 */
type RateOverTimeProps = {
  experiment: Experiment;
  manager: ReturnType<typeof import('@/state/experiments/useExperimentStats').useExperimentStats>;
};

type View = 'cumulative' | 'daily' | 'significance';

type ViewOption = { key: View; label: string; explain: string };

const VIEWS: ViewOption[] = [
  {
    key: 'cumulative',
    label: 'Cumulative rate',
    explain:
      'Conversion rate to date, with 95% confidence bands. Bands that have narrowed and separated mean the result has settled; bands still overlapping mean it has not, however far apart the lines look.',
  },
  {
    key: 'daily',
    label: 'Daily rate',
    explain:
      'Each day on its own. The cumulative view dilutes every day by all the days before it, so a variant that won in week one and reverted later still looks like a mild winner. This is the only view where that shows.',
  },
  {
    key: 'significance',
    label: 'Significance over time',
    explain:
      'The p-value against the control, recomputed each day. A line that dips below 0.05 and comes back up shows that stopping on the first significant day would have been stopping on noise.',
  },
];

const asPercent = (value: number) => `${(value * 100).toFixed(2)}%`;

const VIEW_OPTIONS = VIEWS.map((option) => ({ value: option.key, label: option.label }));

/** Narrows the pill's string back to a View, so the switcher needs no cast. */
const isView = (value: string): value is View => VIEWS.some((option) => option.key === value);

export const RateOverTime = ({ experiment, manager }: RateOverTimeProps) => {
  const [view, setView] = useState<View>('cumulative');
  const { points, sessionsFrom, variants, isLoading } = useExperimentTimeseries(experiment, manager.currency);

  const controlId = variants.find((v) => v.isControl)?.id ?? variants[0]?.id ?? 0;

  const variantIds = variants.map((v) => v.id);

  /*
    Built from the experiment, not from `variants` above: the timeseries endpoint drops
    deployed variants, so ranking within this list would give a variant a different colour
    here than on the charts beside it the moment a rollout exists.
  */
  const colorOrder = variantColorOrder(experiment.variants ?? []);

  // Days before session history begins carry conversions with no denominator; folding
  // them in put the whole pre-rollup conversion history over post-rollup sessions.
  const covered = sinceSessionsStart(points, sessionsFrom);
  const cumulative = buildCumulativeSeries(covered, variantIds);
  const daily = buildDailySeries(covered, variantIds);

  const challenger = variants.find((v) => v.id !== controlId);
  const significance = challenger ? buildSignificanceSeries(covered, controlId, challenger.id) : [];
  const crossings = countSignificanceCrossings(significance);

  if (isLoading) {
    return (
      <div className="border-line bg-panel2 rounded-card border px-4 py-10 text-center">
        <p className="supporting">Loading daily history…</p>
      </div>
    );
  }

  // Conversions have years of history but sessions only exist from the hourly rollup
  // onward, so an experiment that ended before it shipped has no rate to draw. Say
  // that, rather than showing an empty chart that reads as "no traffic".
  const hasRates = cumulative.some((point) => variantIds.some((id) => point.rates[id] !== undefined));

  if (points.length === 0 || !hasRates) {
    return (
      <div className="border-line bg-panel2 rounded-card space-y-2 border px-4 py-4">
        <p className="supporting flex items-start gap-2">
          <IconInfoCircle className="mt-0.5 size-3.5 shrink-0" />
          <span className="max-w-3xl">
            No daily session history for this experiment{sessionsFrom ? ` before ${sessionsFrom}` : ''}. Conversions are
            recorded per day, but sessions per variant per day only started being kept recently, and the source rows are
            discarded once aggregated, so earlier days cannot be reconstructed.
          </span>
        </p>
        <p className="supporting max-w-3xl">
          Experiments running from now on will fill these charts in as they collect traffic.
        </p>
      </div>
    );
  }

  const active = VIEWS.find((v) => v.key === view)!;

  // Flatten into the shape recharts wants: one row per date, one key per variant.
  const chartData =
    view === 'significance'
      ? significance.map((point) => ({ date: point.date, pValue: point.pValue ?? undefined }))
      : (view === 'cumulative' ? cumulative : daily).map((point) => {
          const row: Record<string, string | number | undefined | [number, number]> = { date: point.date };
          for (const id of variantIds) {
            row[`v${id}`] = point.rates[id];
            if (view === 'cumulative') {
              const rate = point.rates[id];
              const m = (point as (typeof cumulative)[number]).margins[id];
              // Recharts draws a band from an [low, high] tuple on one Area.
              if (rate !== undefined && m !== undefined) row[`band${id}`] = [Math.max(0, rate - m), rate + m];
            }
          }
          return row;
        });

  return (
    <TooltipProvider>
      <div className="border-line bg-panel2 rounded-card border">
        <div className="border-line space-y-3 border-b px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="max-w-2xl space-y-1">
              <h3 className="text-foreground text-title font-medium tracking-[-0.01em]">Rate over time</h3>
              <p className="supporting">{active.explain}</p>
            </div>

            {/* Three views of the same data, so they re-scope what is below rather
                than navigate: pills, like every other scope control here. */}
            <PillTabs
              options={VIEW_OPTIONS}
              value={view}
              onChange={(value) => {
                if (isView(value)) setView(value);
              }}
            />
          </div>

          {view === 'significance' && crossings > 1 && (
            <p className="border-warn-line bg-panel text-warn rounded-card flex items-start gap-2 border border-l-2 px-3 py-2 text-body leading-relaxed">
              <IconAlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <span>
                This result crossed the 0.05 line {crossings} times. Stopping the first time it went below would have
                been stopping on noise, which is exactly what makes checking results repeatedly unsafe.
              </span>
            </p>
          )}
        </div>

        <div className="p-4">
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={AXIS_TICK}
                  tickMargin={8}
                  minTickGap={24}
                  stroke="var(--line)"
                  tickLine={false}
                />
                <YAxis
                  tick={AXIS_TICK}
                  width={64}
                  stroke="var(--line)"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={view === 'significance' ? (v: number) => v.toFixed(2) : (v: number) => asPercent(v)}
                  domain={view === 'significance' ? [0, 1] : ['auto', 'auto']}
                />
                <ChartTooltip
                  cursor={{ stroke: 'var(--line)', strokeWidth: 1 }}
                  contentStyle={TOOLTIP_CONTENT_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  formatter={(value: unknown, name?: string) => {
                    const label = name ?? '';
                    if (Array.isArray(value)) return [`${asPercent(value[0])} to ${asPercent(value[1])}`, '95% band'];
                    if (typeof value !== 'number') return ['-', label];
                    return [view === 'significance' ? value.toFixed(4) : asPercent(value), label];
                  }}
                />
                <Legend wrapperStyle={LEGEND_STYLE} iconType="plainline" iconSize={12} />

                {view === 'significance' ? (
                  <>
                    {/* The decision line, so a crossing is visible rather than
                        inferred. It is a threshold, not a series, so it takes the
                        warning token rather than a series colour. */}
                    <ReferenceLine
                      y={0.05}
                      stroke="hsl(var(--warn))"
                      strokeDasharray="4 4"
                      label={{ value: '0.05', fontSize: 11, fill: 'hsl(var(--dim))' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="pValue"
                      name="p-value"
                      stroke={seriesColor(0)}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  </>
                ) : (
                  variantIds.map((id, index) => {
                    const colour = seriesColorForVariant(id, colorOrder);
                    const name = variants.find((v) => v.id === id)?.name ?? `Variant ${id}`;
                    return (
                      <Fragment key={id}>
                        {view === 'cumulative' && (
                          <Area
                            key={`band-${id}`}
                            type="monotone"
                            dataKey={`band${id}`}
                            stroke="none"
                            fill={colour}
                            fillOpacity={0.12}
                            legendType="none"
                            tooltipType="none"
                            connectNulls
                          />
                        )}
                        <Line
                          key={`line-${id}`}
                          type="monotone"
                          dataKey={`v${id}`}
                          name={name}
                          stroke={colour}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      </Fragment>
                    );
                  })
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {sessionsFrom && (
            <p className="supporting max-w-3xl pt-3">
              Session history begins {sessionsFrom}; earlier days have conversions but no session records, so they are
              left out of these charts entirely.
            </p>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
